/**
 * FlowSummaryBar Component
 * WP-DASH-2: Summary statistics bar for mini Sankey visualization
 *
 * Displays net savings/overspending and savings rate at the bottom
 * of the mini Sankey card.
 */

import { memo } from 'react';
import { formatCurrency } from '@/utils/format';

// ============================================================================
// Types
// ============================================================================

interface FlowSummaryBarProps {
  totalIncome: number;
  totalExpenses: number;
  netFlow: number;
  savingsRate: number;
}

// ============================================================================
// Component
// ============================================================================

/**
 * FlowSummaryBar - Shows summary statistics for the flow
 *
 * Displays:
 * - Net savings or overspending amount
 * - Savings rate as percentage of income
 */
function FlowSummaryBarComponent({
  totalIncome,
  totalExpenses: _totalExpenses,
  netFlow,
  savingsRate,
}: FlowSummaryBarProps) {
  // Note: _totalExpenses is available for future use (e.g., breakdown display)
  const isPositive = netFlow >= 0;

  // Don't show savings rate if there's no income
  const showSavingsRate = totalIncome > 0 && savingsRate > 0;

  return (
    <div className="mt-4 flex items-center justify-between rounded-lg bg-gray-50 p-3">
      <div className="flex items-center gap-2">
        {/* Indicator icon */}
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full ${
            isPositive ? 'bg-green-100' : 'bg-red-100'
          }`}
        >
          {isPositive ? (
            <svg
              className="h-4 w-4 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 10l7-7m0 0l7 7m-7-7v18"
              />
            </svg>
          ) : (
            <svg
              className="h-4 w-4 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          )}
        </div>

        {/* Amount */}
        <div>
          <span className="text-sm text-gray-600">
            {isPositive ? 'Saved' : 'Overspent'}:{' '}
          </span>
          <span
            className={`font-semibold ${
              isPositive ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {formatCurrency(Math.abs(netFlow))}
          </span>
        </div>
      </div>

      {/* Savings rate */}
      {showSavingsRate && (
        <div className="text-sm text-gray-500">
          <span className="font-medium">{Math.round(savingsRate)}%</span> of income
        </div>
      )}
    </div>
  );
}

export const FlowSummaryBar = memo(FlowSummaryBarComponent);

export default FlowSummaryBar;
